import React, { createContext, useContext, useReducer, useMemo, useCallback, useEffect, useState } from 'react'
import { client } from '../apollo/client'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useTimeframe } from './Application'
import {
  getPercentChange,
  getBlockFromTimestamp,
  getBlocksFromTimestamps,
  get2DayPercentChange,
  getTimeframe,
  isNRGTokenAddress,
} from '../utils'
import {
  GLOBAL_DATA,
  GLOBAL_MINTS,
  GLOBAL_BURNS,
  GLOBAL_SWAPS,
  GLOBAL_CHART,
  GLOBAL_DAILY_STATS,
  NRG_PRICE,
  ALL_PAIRS,
  ALL_TOKENS,
  TOP_LPS_PER_PAIRS,
} from '../apollo/queries'
import { fetchHourlyPairVolumes } from '../utils/hourlyStats'
import useTransactionFeed from '../hooks/useTransactionFeed'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { useAllPairData } from './PairData'
import replaceSymbolE from '../utils/replace-symbol-e'
import Web3 from 'web3'
import ROUTER_PROXY_ABI from '../constants/abi/EnergiswapRouterGovernedProxy.json'
import STAKING_REWARDS_MANAGER_ABI from '../constants/abi/StakingRewardsManagerStorage.json'
import getEnvironmentVar from '../utils/getEnvironmentVar'
import { LAUNCH_DATE_UTC } from '../constants'

require('dotenv').config()

const UPDATE = 'UPDATE'
const UPDATE_CHART = 'UPDATE_CHART'
const UPDATE_NRG_PRICE = 'UPDATE_NRG_PRICE'
const NRG_PRICE_KEY = 'NRG_PRICE_KEY'
const UPDATE_ALL_PAIRS_IN_ENERGISWAP = 'UPDATE_ALL_PAIRS_IN_ENERGISWAP'
const UPDATE_ALL_TOKENS_IN_ENERGISWAP = 'UPDATE_ALL_TOKENS_IN_ENERGISWAP'
const UPDATE_TOP_LPS = 'UPDATE_TOP_LPS'
const UPDATE_ROUTER_IMPL_ADDRESS = 'UPDATE_ROUTER_IMPL_ADDRESS'
const UPDATE_STAKING_CONTRACT_ADDRESSES = 'UPDATE_STAKING_CONTRACT_ADDRESSES'

// format dayjs with the libraries that we need
dayjs.extend(utc)
dayjs.extend(weekOfYear)

const GlobalDataContext = createContext()

function useGlobalDataContext() {
  return useContext(GlobalDataContext)
}

function reducer(state, { type, payload }) {
  switch (type) {
    case UPDATE: {
      const { data } = payload
      return {
        ...state,
        globalData: data,
      }
    }
    case UPDATE_CHART: {
      const { daily, weekly } = payload
      return {
        ...state,
        chartData: {
          daily,
          weekly,
        },
      }
    }
    case UPDATE_NRG_PRICE: {
      const { nrgPrice, oneDayPrice, nrgPriceChange } = payload
      return {
        [NRG_PRICE_KEY]: nrgPrice,
        oneDayPrice,
        nrgPriceChange,
      }
    }

    case UPDATE_ALL_PAIRS_IN_ENERGISWAP: {
      const { allPairs } = payload
      return {
        ...state,
        allPairs,
      }
    }

    case UPDATE_ALL_TOKENS_IN_ENERGISWAP: {
      const { allTokens } = payload
      return {
        ...state,
        allTokens,
      }
    }

    case UPDATE_TOP_LPS: {
      const { topLps } = payload
      return {
        ...state,
        topLps,
      }
    }
    case UPDATE_ROUTER_IMPL_ADDRESS: {
      return {
        ...state,
        energiswapRouterImplAddress: payload.address,
      }
    }
    case UPDATE_STAKING_CONTRACT_ADDRESSES: {
      return {
        ...state,
        stakingContractAddresses: payload.stakingContractAddrList,
      }
    }
    default: {
      throw Error(`Unexpected action type in DataContext reducer: '${type}'.`)
    }
  }
}

export default function Provider({ children }) {
  const [state, dispatch] = useReducer(reducer, {})
  const update = useCallback((data) => {
    dispatch({
      type: UPDATE,
      payload: {
        data,
      },
    })
  }, [])

  const updateChart = useCallback((daily, weekly) => {
    dispatch({
      type: UPDATE_CHART,
      payload: {
        daily,
        weekly,
      },
    })
  }, [])

  const updateNrgPrice = useCallback((nrgPrice, oneDayPrice, nrgPriceChange) => {
    dispatch({
      type: UPDATE_NRG_PRICE,
      payload: {
        nrgPrice,
        oneDayPrice,
        nrgPriceChange,
      },
    })
  }, [])

  const updateAllPairsInEnergiswap = useCallback((allPairs) => {
    dispatch({
      type: UPDATE_ALL_PAIRS_IN_ENERGISWAP,
      payload: {
        allPairs,
      },
    })
  }, [])

  const updateAllTokensInEnergiswap = useCallback((allTokens) => {
    dispatch({
      type: UPDATE_ALL_TOKENS_IN_ENERGISWAP,
      payload: {
        allTokens,
      },
    })
  }, [])

  const updateTopLps = useCallback((topLps) => {
    dispatch({
      type: UPDATE_TOP_LPS,
      payload: {
        topLps,
      },
    })
  }, [])

  const updateRouterImplAddress = useCallback((address) => {
    dispatch({
      type: UPDATE_ROUTER_IMPL_ADDRESS,
      payload: {
        address,
      },
    })
  }, [])

  const updateStakingContractAdresses = useCallback((stakingContractAddrList) => {
    dispatch({
      type: UPDATE_STAKING_CONTRACT_ADDRESSES,
      payload: {
        stakingContractAddrList,
      },
    })
  }, [])

  return (
    <GlobalDataContext.Provider
      value={useMemo(
        () => [
          state,
          {
            update,
            updateChart,
            updateNrgPrice,
            updateTopLps,
            updateAllPairsInEnergiswap,
            updateAllTokensInEnergiswap,
            updateRouterImplAddress,
            updateStakingContractAdresses,
          },
        ],
        [
          state,
          update,
          updateTopLps,
          updateChart,
          updateNrgPrice,
          updateAllPairsInEnergiswap,
          updateAllTokensInEnergiswap,
          updateRouterImplAddress,
          updateStakingContractAdresses,
        ]
      )}
    >
      {children}
    </GlobalDataContext.Provider>
  )
}

/**
 * Gets all the global data for the overview page.
 * Needs current NRG price and the old NRG price to get
 * 24 hour USD changes.
 * @param {*} nrgPrice
 * @param {*} oldNrgPrice
 */
async function getGlobalData(nrgPrice, oldNrgPrice) {
  // data for each day , historic data used for % changes
  let data = {}
  let oneDayStatistic = {}
  let twoDayStatistic = {}

  try {
    // get timestamps for the days
    const utcCurrentTime = dayjs()
    const utcOneDayBack = utcCurrentTime.subtract(1, 'day').unix()
    const utcTwoDaysBack = utcCurrentTime.subtract(2, 'day').unix()
    const utcOneWeekBack = utcCurrentTime.subtract(1, 'week').unix()
    const utcTwoWeeksBack = utcCurrentTime.subtract(2, 'week').unix()

    // get the blocks needed for time travel queries
    let [oneDayBlock, twoDayBlock, oneWeekBlock, twoWeekBlock] = await getBlocksFromTimestamps([
      utcOneDayBack,
      utcTwoDaysBack,
      utcOneWeekBack,
      utcTwoWeeksBack,
    ])

    const emptyResult = { data: { energiswapFactory: null } }

    // fetch the global data
    const result = await client.query({
      query: GLOBAL_DATA(),
      fetchPolicy: 'cache-first',
    })

    // fetch the historical data — block-based queries may not be supported by all indexers
    let [oneDayResult, twoDayResult, oneWeekResult, twoWeekResult] = await Promise.all([
      client.query({ query: GLOBAL_DATA(oneDayBlock?.number), fetchPolicy: 'cache-first' }).catch(() => emptyResult),
      client.query({ query: GLOBAL_DATA(twoDayBlock?.number), fetchPolicy: 'cache-first' }).catch(() => emptyResult),
      client.query({ query: GLOBAL_DATA(oneWeekBlock?.number), fetchPolicy: 'cache-first' }).catch(() => emptyResult),
      client.query({ query: GLOBAL_DATA(twoWeekBlock?.number) }).catch(() => emptyResult),
    ])

    data = result.data.energiswapFactory

    const emptyData = {
      id: '1',
      pairCount: 0,
      totalLiquidityNRG: 0,
      totalLiquidityUSD: 0,
      totalVolumeNRG: 0,
      totalVolumeUSD: 0,
      txCount: 0,
      untrackedVolumeUSD: 0,
    }

    if (oneDayResult.data.energiswapFactory) {
      oneDayStatistic = oneDayResult.data.energiswapFactory
    } else {
      oneDayStatistic = emptyData
    }

    if (twoDayResult.data.energiswapFactory) {
      twoDayStatistic = twoDayResult.data.energiswapFactory
    } else {
      twoDayStatistic = emptyData
    }

    let oneWeekData
    if (oneWeekResult.data.energiswapFactory) {
      oneWeekData = oneWeekResult.data.energiswapFactory
    } else {
      oneWeekData = emptyData
    }

    let twoWeekData
    if (twoWeekResult.data.energiswapFactory) {
      twoWeekData = twoWeekResult.data.energiswapFactory
    } else {
      twoWeekData = emptyData
    }

    if (data && oneDayStatistic && twoDayStatistic && oneWeekData && twoWeekData) {
      let [oneDayVolumeUSD, volumeChangeUSD] = get2DayPercentChange(
        data.totalVolumeUSD,
        oneDayStatistic.totalVolumeUSD ? oneDayStatistic.totalVolumeUSD : 0,
        twoDayStatistic.totalVolumeUSD ? twoDayStatistic.totalVolumeUSD : 0
      )

      const [oneWeekVolume, weeklyVolumeChange] = get2DayPercentChange(
        data.totalVolumeUSD,
        oneWeekData.totalVolumeUSD,
        twoWeekData.totalVolumeUSD
      )

      const [oneDayTxns, txnChange] = get2DayPercentChange(
        data.txCount,
        oneDayStatistic.txCount ? oneDayStatistic.txCount : 0,
        twoDayStatistic.txCount ? twoDayStatistic.txCount : 0
      )

      // format the total liquidity in USD
      data.totalLiquidityUSD = data.totalLiquidityNRG * nrgPrice
      const liquidityChangeUSD = getPercentChange(
        data.totalLiquidityNRG * nrgPrice,
        oneDayStatistic.totalLiquidityNRG * oldNrgPrice
      )

      // add relevant fields with the calculated amounts
      data.oneDayVolumeUSD = oneDayVolumeUSD
      data.oneWeekVolume = oneWeekVolume
      data.weeklyVolumeChange = weeklyVolumeChange
      data.volumeChangeUSD = volumeChangeUSD
      data.liquidityChangeUSD = liquidityChangeUSD
      data.oneDayTxns = oneDayTxns
      data.txnChange = txnChange
    }
  } catch (e) {
    console.log(e)
  }

  // Override volume + % change + liquidity using 48h of hourly pair stats (same approach as production)
  try {
    const { totalVolume, prevTotalVolume } = await fetchHourlyPairVolumes()
    if (totalVolume > 0 && data) {
      data.oneDayVolumeUSD = totalVolume
      data.volumeChangeUSD = getPercentChange(totalVolume, prevTotalVolume)
    }
    // Liquidity % change + weekly volume from daily stats
    try {
      const dailyResult = await client.query({ query: GLOBAL_DAILY_STATS, fetchPolicy: 'cache-first' })
      const dailyStats = dailyResult?.data?.dailyEnergiswapStatistics ?? []

      // Liquidity % change: today vs yesterday
      const [today, yesterday] = dailyStats
      if (today && yesterday && data) {
        data.liquidityChangeUSD = getPercentChange(
          parseFloat(today.totalLiquidityUSD ?? 0),
          parseFloat(yesterday.totalLiquidityUSD ?? 0)
        )
      }

      // Weekly volume: sum last 7 days vs previous 7 days
      if (dailyStats.length >= 2 && data) {
        const currentWeek = dailyStats.slice(0, 7).reduce((sum, d) => sum + parseFloat(d.dailyVolumeUSD ?? 0), 0)
        const prevWeek = dailyStats.slice(7, 14).reduce((sum, d) => sum + parseFloat(d.dailyVolumeUSD ?? 0), 0)
        if (currentWeek > 0) {
          data.oneWeekVolume = currentWeek
          data.weeklyVolumeChange = getPercentChange(currentWeek, prevWeek)
        }
      }
    } catch (e) {}

    // Transactions (24H): true rolling 24h window (mints+burns+swaps), not a UTC-calendar-day diff -
    // dailyEnergiswapStatistics.txCount only tells us "since midnight", which undercounts for most of the day.
    try {
      const nowUnix = dayjs().unix()
      const [current, previous] = await Promise.all([
        getGlobalTransactions(nowUnix - 86400, nowUnix, 1000),
        getGlobalTransactions(nowUnix - 172800, nowUnix - 86400, 1000),
      ])
      const countTxns = (t) => (t.mints?.length ?? 0) + (t.burns?.length ?? 0) + (t.swaps?.length ?? 0)
      const oneDayTxns = countTxns(current)
      const twoDayTxns = countTxns(previous)
      if (data) {
        data.oneDayTxns = oneDayTxns
        data.txnChange = getPercentChange(oneDayTxns, twoDayTxns)
      }
    } catch (e) {}
  } catch (e) {}

  // launch reset: on launch day itself there's no real "yesterday" - the
  // comparisons above would reach into pre-launch data. Zero the badges for
  // that one day only; from the day after, today's data has been collected
  // since launch so the real comparison above is correct again.
  const launchDayStart = Math.floor(
    Date.UTC(LAUNCH_DATE_UTC.year, LAUNCH_DATE_UTC.month, LAUNCH_DATE_UTC.day) / 1000
  )
  const isLaunchDay = dayjs().unix() < launchDayStart + 24 * 60 * 60
  if (data && isLaunchDay) {
    data.liquidityChangeUSD = 0
    data.volumeChangeUSD = 0
    data.weeklyVolumeChange = 0
    data.txnChange = 0
  }

  return data
}

/**
 * Get historical data for volume and liquidity used in global charts
 * on main page
 * @param {*} oldestDateToFetch // start of window to fetch from
 */
const getChartData = async (oldestDateToFetch) => {
  // oldestDateToFetch = 1608000000 // Hack to bypass oldest blocks
  let data = []
  let weeklyData = []
  const utcEndTime = dayjs.utc()
  let skip = 0
  let allFound = false

  try {
    while (!allFound) {
      let result = await client.query({
        query: GLOBAL_CHART,
        variables: {
          startTime: String(oldestDateToFetch),
          skip,
        },
        fetchPolicy: 'cache-first',
      })
      skip += 1000
      data = data.concat(result.data.dailyEnergiswapStatistics)
      if (result.data.dailyEnergiswapStatistics.length < 1000) {
        allFound = true
      }
    }

    if (data) {
      let dayIndexSet = new Set()
      let dayIndexArray = []
      const oneDay = 24 * 60 * 60

      // for each day, parse the daily volume and format for chart array
      data.forEach((dailyStatistic, i) => {
        // add the day index to the set of days
        dayIndexSet.add((data[i].date / oneDay).toFixed(0))
        dayIndexArray.push(data[i])
        dailyStatistic.dailyVolumeUSD = parseFloat(dailyStatistic.dailyVolumeUSD)
      })

      // fill in empty days ( there will be no day datas if no trades made that day )
      let timestamp = data[0].date ? data[0].date : oldestDateToFetch
      let latestLiquidityUSD = data[0].totalLiquidityUSD
      let latestDayDats = data[0].mostLiquidTokens
      let index = 1
      while (timestamp < utcEndTime.unix() - oneDay) {
        const nextDay = timestamp + oneDay
        let currentDayIndex = (nextDay / oneDay).toFixed(0)
        if (!dayIndexSet.has(currentDayIndex)) {
          data.push({
            date: nextDay,
            dailyVolumeUSD: 0,
            totalLiquidityUSD: latestLiquidityUSD,
            mostLiquidTokens: latestDayDats,
          })
        } else {
          latestLiquidityUSD = dayIndexArray[index].totalLiquidityUSD
          latestDayDats = dayIndexArray[index].mostLiquidTokens
          index = index + 1
        }
        timestamp = nextDay
      }
    }

    // format weekly data for weekly sized chunks
    data = data.sort((a, b) => (parseInt(a.date) > parseInt(b.date) ? 1 : -1))
    let startIndexWeekly = -1
    let currentWeek = -1
    data.forEach((entry, i) => {
      const week = dayjs.utc(dayjs.unix(data[i].date)).week()
      if (week !== currentWeek) {
        currentWeek = week
        startIndexWeekly++
      }
      weeklyData[startIndexWeekly] = weeklyData[startIndexWeekly] || {}
      weeklyData[startIndexWeekly].date = data[i].date
      weeklyData[startIndexWeekly].weeklyVolumeUSD =
        (weeklyData[startIndexWeekly].weeklyVolumeUSD ?? 0) + data[i].dailyVolumeUSD
    })
  } catch (e) {
    console.log(e)
  }
  return [data, weeklyData]
}

/**
 * Get one time-windowed batch of global transactions, walking either newest-to-oldest
 * (direction='desc', the default) or oldest-to-newest (direction='asc').
 */
const getGlobalTransactions = async (windowStart, windowEnd, first, direction = 'desc') => {
  let transactions = {}

  try {
    const variables = { first, windowStart: String(windowStart), windowEnd: String(windowEnd), orderDirection: direction }
    const [mintsResult, burnsResult, swapsResult] = await Promise.all([
      client.query({
        query: GLOBAL_MINTS,
        variables,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: GLOBAL_BURNS,
        variables,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: GLOBAL_SWAPS,
        variables,
        fetchPolicy: 'cache-first',
      }),
    ])
    transactions.mints = mintsResult?.data?.mints ?? []
    transactions.burns = burnsResult?.data?.burns ?? []
    transactions.swaps = swapsResult?.data?.swaps ?? []
  } catch (e) {
    console.log(e)
  }

  return transactions
}

/**
 * Gets the current NRG price, the price from 24h before, and the % change between them
 */

const getNrgPrice = async () => {
  const utcCurrentTime = dayjs()
  const utcOneDayBack = utcCurrentTime.subtract(1, 'day').startOf('minute').unix()

  let nrgPrice = 0
  let nrgPriceOneDay = 0
  let priceChangeNRG = 0

  try {
    const result = await client.query({
      query: NRG_PRICE(),
      fetchPolicy: 'cache-first',
    })
    nrgPrice = result?.data?.bundle?.nrgUsdPrice
    nrgPriceOneDay = nrgPrice
  } catch (e) {
    console.log(e)
  }

  try {
    const oneDayBlock = await getBlockFromTimestamp(utcOneDayBack)
    const resultOneDay = await client.query({
      query: NRG_PRICE(oneDayBlock),
      fetchPolicy: 'cache-first',
    })
    const oneDayBackPrice = resultOneDay?.data?.bundle?.nrgUsdPrice
    if (oneDayBackPrice) {
      nrgPriceOneDay = oneDayBackPrice
      priceChangeNRG = getPercentChange(nrgPrice, oneDayBackPrice)
    }
  } catch (e) {
    // block-based historical queries not supported by this indexer
  }
  return [nrgPrice, nrgPriceOneDay, priceChangeNRG]
}

const PAIRS_TO_FETCH = 500
const TOKENS_TO_FETCH = 500

/**
 * Loop through every pair on energiswap, used for search
 */
async function getAllPairsOnEnergiswap() {
  try {
    let allFound = false
    let pairs = []
    let skipCount = 0
    while (!allFound) {
      let result = await client.query({
        query: ALL_PAIRS,
        variables: {
          skip: skipCount,
        },
        fetchPolicy: 'cache-first',
      })
      skipCount = skipCount + PAIRS_TO_FETCH
      pairs = pairs.concat(result?.data?.pairs)
      if (result?.data?.pairs.length < PAIRS_TO_FETCH || pairs.length > PAIRS_TO_FETCH) {
        allFound = true
      }
    }
    return pairs
  } catch (e) {
    console.log(e)
  }
}

/**
 * Loop through every token on energiswap, used for search
 */
async function getAllTokensOnEnergiswap() {
  try {
    let allFound = false
    let skipCount = 0
    let tokens = []
    while (!allFound) {
      let result = await client.query({
        query: ALL_TOKENS,
        variables: {
          skip: skipCount,
        },
        fetchPolicy: 'cache-first',
      })
      tokens = tokens.concat(result?.data?.tokens)
      if (result?.data?.tokens?.length < TOKENS_TO_FETCH || tokens.length > TOKENS_TO_FETCH) {
        allFound = true
      }
      skipCount = skipCount += TOKENS_TO_FETCH
    }
    return tokens
  } catch (e) {
    console.log(e)
  }
}

/**
 * Hook that fetches overview data, plus all tokens and pairs for search
 */
export function useGlobalData() {
  const [state, { update, updateAllPairsInEnergiswap, updateAllTokensInEnergiswap }] = useGlobalDataContext()
  const [nrgPrice, oldNrgPrice] = useNrgPrice()

  const data = state?.globalData
  // console.log('data: ', data)

  useMemo(() => {
    async function fetchData() {
      const [globalData, allPairs, allTokens] = await Promise.all([
        getGlobalData(nrgPrice, oldNrgPrice),
        getAllPairsOnEnergiswap(),
        getAllTokensOnEnergiswap(),
      ])
      globalData && update(globalData)
      updateAllPairsInEnergiswap(allPairs)
      updateAllTokensInEnergiswap(allTokens)
    }
    if (!data && nrgPrice && oldNrgPrice) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nrgPrice, oldNrgPrice, data])
  return data || {}
}

export function useGlobalChartData() {
  const [state, { updateChart }] = useGlobalDataContext()
  const [oldestDateFetch, setOldestDateFetched] = useState()
  const [activeWindow] = useTimeframe()

  const chartDataDaily = state?.chartData?.daily
  const chartDataWeekly = state?.chartData?.weekly

  /**
   * Keep track of oldest date fetched. Used to
   * limit data fetched until its actually needed.
   * (dont fetch year long stuff unless year option selected)
   */
  useEffect(() => {
    // based on window, get starttime
    let startTime = getTimeframe(activeWindow)

    if ((activeWindow && startTime < oldestDateFetch) || !oldestDateFetch) {
      setOldestDateFetched(startTime)
    }
  }, [activeWindow, oldestDateFetch])

  /**
   * Fetch data if none fetched or older data is needed
   */
  useEffect(() => {
    async function fetchData() {
      // historical stuff for chart
      let [newChartData, newWeeklyData] = await getChartData(oldestDateFetch)
      updateChart(newChartData, newWeeklyData)
    }
    if (oldestDateFetch && !(chartDataDaily && chartDataWeekly)) {
      fetchData()
    }
  }, [chartDataDaily, chartDataWeekly, oldestDateFetch, updateChart])

  return [chartDataDaily, chartDataWeekly]
}

export function useGlobalTransactions() {
  return useTransactionFeed(getGlobalTransactions, 'global')
}

export function useNrgPrice() {
  const [state, { updateNrgPrice }] = useGlobalDataContext()
  const nrgPrice = state?.[NRG_PRICE_KEY]
  const nrgPriceOld = state?.oneDayPrice

  useEffect(() => {
    async function checkForNrgPrice() {
      if (!nrgPrice) {
        let [newPrice, oneDayPrice, priceChange] = await getNrgPrice()
        if (!oneDayPrice) {
          oneDayPrice = newPrice
        }
        updateNrgPrice(newPrice, oneDayPrice, priceChange)
      }
    }

    checkForNrgPrice()
  }, [nrgPrice, nrgPriceOld, updateNrgPrice])

  return [nrgPrice, nrgPriceOld]
}

export function useAllPairsInEnergiswap() {
  const [state] = useGlobalDataContext()
  let allPairs = state?.allPairs

  return allPairs || []
}

export function useAllTokensInEnergiswap() {
  const [state] = useGlobalDataContext()
  let allTokens = state?.allTokens

  return allTokens || []
}

/**
 * Gets the EnergiswapRouter Implementation address
 */
export function useEnergiswapRouterImplAddress() {
  const [state, { updateRouterImplAddress }] = useGlobalDataContext()
  let address = state?.energiswapRouterImplAddress

  useEffect(() => {
    async function getAddress() {
      const web3 = new Web3(getEnvironmentVar('NETWORK_URL'))
      const routerProxyContract = new web3.eth.Contract(ROUTER_PROXY_ABI, getEnvironmentVar('ROUTER_PROXY_ADDRESS'))
      const resultAddress = await routerProxyContract.methods.impl().call()
      updateRouterImplAddress(resultAddress)
    }

    if (!address) {
      getAddress()
    }
  }, [address, updateRouterImplAddress])

  return address
}

/**
 * Gets the staking rewards contract addresses using the StakingRewardsManagerStorage contract
 */
export function useStakingRewardsAddresses() {
  const [state, { updateStakingContractAdresses }] = useGlobalDataContext()
  let stakingContractAddrList = state?.stakingContractAddresses

  useEffect(() => {
    async function getAddresses() {
      try {
        const addresses = []
        const web3 = new Web3(getEnvironmentVar('NETWORK_URL'))
        const stakingRewardsManagerStorage = new web3.eth.Contract(
          STAKING_REWARDS_MANAGER_ABI,
          getEnvironmentVar('STAKING_REWARD_MANAGER_ADDRESS')
        )

        const numberOfAddresses = await stakingRewardsManagerStorage.methods.getAllStakingRewardsProxyLength().call()
        const addressesLength = parseInt(numberOfAddresses ?? 0)

        if (addressesLength > 0) {
          const indexes = [...Array(addressesLength).keys()]
          const proxyAddresses = await Promise.all(
            indexes.map((index) => stakingRewardsManagerStorage.methods.getAllStakingRewardsProxy(index).call())
          )

          addresses.push(
            ...proxyAddresses.map((address) => {
              return address.toLowerCase()
            })
          )
        }
        updateStakingContractAdresses(addresses)
      } catch (e) {
        console.log(e)
      }
    }
    if (!stakingContractAddrList) {
      getAddresses()
    }
  }, [stakingContractAddrList, updateStakingContractAdresses])

  return stakingContractAddrList
}

/**
 * Get the top liquidity positions based on USD size
 * @TODO Not a perfect lookup, needs improvement
 */
export function useTopLps() {
  const [state, { updateTopLps }] = useGlobalDataContext()
  let topLps = state?.topLps

  const allPairs = useAllPairData()

  useEffect(() => {
    async function fetchData() {
      // get top 20 by reserves
      let topPairs = Object.keys(allPairs)
        ?.sort((a, b) => (parseFloat(allPairs[a].reserveUSD) > parseFloat(allPairs[b].reserveUSD) ? -1 : 1))
        ?.slice(0, 99)
        .map((pair) => pair)

      let topLpLists = await Promise.all(
        topPairs.map(async (pair) => {
          // for each one, fetch top LPs
          try {
            const { data: results } = await client.query({
              query: TOP_LPS_PER_PAIRS,
              variables: {
                pair: pair.toString(),
              },
              fetchPolicy: 'cache-first',
            })
            if (results) {
              return results.liquidityPositions
            }
          } catch (e) { }
        })
      )

      // get the top lps from the results formatted
      const topLps = []
      topLpLists
        .filter((i) => !!i) // check for ones not fetched correctly
        .map((list) => {
          return list.map((entry) => {
            const pairData = allPairs[entry.pair.id]

            let pairName
            let swappedTickers
            let symbol0 = replaceSymbolE({ id: pairData.token0.id, symbol: pairData.token0.symbol })
            let symbol1 = replaceSymbolE({ id: pairData.token1.id, symbol: pairData.token1.symbol })

            // Display NRG symbol instead of WNRG
            if (isNRGTokenAddress(pairData.token0.id)) {
              symbol0 = 'NRG'
            }
            if (isNRGTokenAddress(pairData.token1.id)) {
              symbol1 = 'NRG'
            }
            swappedTickers = symbol1 === 'NRG'
            pairName = swappedTickers ? 'NRG-' + symbol0 : symbol0 + '-' + symbol1

            return topLps.push({
              user: entry.user,
              pairName,
              swappedTickers,
              pairAddress: entry.pair.id,
              token0: pairData.token0.id,
              token1: pairData.token1.id,
              usd:
                (parseFloat(entry.liquidityTokenBalance) / parseFloat(pairData.totalSupply)) *
                parseFloat(pairData.reserveUSD),
            })
          })
        })

      const sorted = topLps.sort((a, b) => (a.usd > b.usd ? -1 : 1))
      const shorter = sorted.splice(0, 100)
      updateTopLps(shorter)
    }

    if (!topLps && allPairs && Object.keys(allPairs).length > 0) {
      fetchData()
    }
  })

  return topLps
}
